/**
 * RFC-0089: Yjs-backed canvas state hooks.
 * RFC-0319: Per-field YDoc node data (source of truth).
 *
 * These hooks observe a Y document's "nodes" and "edges" maps and convert them
 * to ReactFlow-compatible data structures. They also provide write functions
 * for updating positions back into the Y doc.
 *
 * The backend computes node positions (via layout.py) and writes them to YDoc.
 * The frontend reads positions directly from YDoc — no client-side layout needed.
 * The frontend writes only x, y, tainted (on drag). Viewport is local UI state
 * and is persisted in browser storage, not YDoc.
 *
 * Per-field reads (RFC-0319): individual in_ and out_ prefixed keys are read
 * directly from each Y.Map node entry with schema-driven deserialization.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import * as Y from 'yjs'
import type { Edge, Node, ReactFlowInstance, Viewport } from 'reactflow'
import { useQueryClient } from '@tanstack/react-query'
import { getCanvasState } from '../api/client'
import {
  CANVAS_HIDDEN_FE_TYPES,
  CANVAS_NODE_FIELD_TYPES,
  NODE_STATE_FIELDS,
  YDOC_STRUCTURAL_KEYS,
  type AppNode,
  type AppNodeData,
  bindCanvasNodeByFeType,
  bindRawCanvasNode,
  isFeNodeType,
  NODE_STATUSES,
  type FeNodeType,
  type NodeStatus,
  type RawYDocNodeByBackendType,
  type ReactFlowNodeDataPatch,
  type ReactFlowNodeRef,
  type YDocInputFlushEdgeRef,
  type YDocInputFlushNode,
  type YDocInputFlushResult,
  type YDocWriteRecord,
  type YDocWriteValue,
  type YDocValue,
} from '../generated/nodeDataTypes'
import {
  deriveGroupedNodeLayout,
} from '../components/canvas-v2/nodes/GroupNode/GroupNode'
import {
  assertNoBackendAuthoredOutputMapWrites,
  assertNoDirectAssetInputPortWrites,
} from '../components/canvas-v2/assetPortGuards'
import {
  referencePairKindFromEdgeType,
  type ReferencePairKind,
} from '../components/canvas-v2/referencePairEdges'
import { queryKeys } from '../api/hooks'
import { createDebug, createNadDebug } from '../utils/debugLog'
import {
  isRenderableCanvasNodeType,
  schemaBackendType,
  toBackendType,
} from '../utils/nodeTypes'
import { CANVAS_AUTOFOCUS_COOLDOWN_MS, isDepthVideoExecutionNode } from './canvasAutoFocus'
import { prepareYDocCreationData } from './ydocCreationDefaults'

const log = createDebug('dw:canvas:ydoc')
const statusLog = createDebug('dw:canvas:status')
const ydocWriteLog = createDebug('dw:ydoc-write:ydoc-write')
const lifecycleLog = createDebug('dw:ydoc:lifecycle')
const layoutLog = createDebug('dw:layout:position')
const libraryImportLog = createNadDebug('dw:library-import:node-status')
const costInvalidateLog = createDebug('dw:cost-summary:invalidate')
const sparksRefreshLog = createNadDebug('dw:credit-balance:refresh')
const chatTaskStatusLog = createNadDebug('dw:chat:task-status')
const viewportFocusLog = createNadDebug('dw:canvas:viewport-focus')
const nodeResyncLog = createNadDebug('dw:canvas:node-resync')
// Label-apply trace (W2): YDoc `label` change → rebuild path → ReactFlow label.
const labelApplyLog = createNadDebug('dw:canvas:label-apply', { priority: true })
// Feature-tagged NAD trace for the YDoc-first deletion path. The rebuild()
// structural-removal branch is the SOLE writer of React node state on delete;
// this log marks where a `frontend` YDoc node-delete transaction drives a full
// rebuild (`nad trace --feature canvas-node-delete`). Dev-gated behind
// VITE_NAD_DEBUG (zero prod cost).
const nodeDeleteRebuildLog = createNadDebug('dw:canvas-node-delete:rebuild')
const autofocusLog = createNadDebug('dw:canvas:autofocus')
const depthBatchLayoutLog = createNadDebug('dw:depth-batch-layout')

export type RawCanvasYNode = Y.Map<YDocValue>
export type RawCanvasYNodeTable = Y.Map<RawCanvasYNode>
type YDocMap = RawCanvasYNode
type YDocMapTable = RawCanvasYNodeTable
type YDocEvent = Y.YEvent<YDocMap>
type BackendNodeType = keyof RawYDocNodeByBackendType
export type ReactFlowFieldValue = YDocWriteValue
type RawNodeDataUpdates = YDocWriteRecord
type NodeDataUpdates<T extends FeNodeType> = ReactFlowNodeDataPatch<T>
type ActiveCanvasScope = {
  ydoc: Y.Doc
  projectId?: string
  canvasId: string
}
export type ReactFlowCanvasNodeData = AppNodeData
export type ReactFlowCanvasNode = AppNode | Node<ReactFlowCanvasNodeData, string | undefined>
export type ReactFlowCanvasEdgeData = {
  toIndex?: number
  fromIndex?: number
  referencePairKind?: ReferencePairKind
}
export type ReactFlowCanvasEdge = Edge<ReactFlowCanvasEdgeData>

export function isRecordLike(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

export function dataValue(data: unknown, key: string): ReactFlowFieldValue | undefined {
  if (data instanceof Y.Map) {
    throw new Error(`dataValue(${key}) received a raw Y.Map; use a generated CanvasNode wrapper`)
  }
  if (!isRecordLike(data)) return undefined
  return data[key]
}

/** Narrowing helpers for reading fields from generated ReactFlow node data. */
export function dataStr(data: unknown, key: string, fallback = ''): string {
  const v = dataValue(data, key)
  return typeof v === 'string' ? v : fallback
}
export function dataStrOpt(data: unknown, key: string): string | undefined {
  const v = dataValue(data, key)
  return typeof v === 'string' ? v : undefined
}
export function dataBool(data: unknown, key: string): boolean | undefined {
  const v = dataValue(data, key)
  return typeof v === 'boolean' ? v : undefined
}
const _nodeStatusSet: ReadonlySet<string> = new Set(NODE_STATUSES)
const SPARKS_REFRESH_STATUSES: ReadonlySet<NodeStatus> = new Set<NodeStatus>([
  'running',
  'completed',
  'error',
])
const RESYNC_COMMON_DATA_FIELDS = new Set([
  'name',
  'error',
  'progress',
  'progressMessage',
  'projectId',
  'cacheKey',
])
function isNodeStatus(value: unknown): value is NodeStatus {
  return typeof value === 'string' && _nodeStatusSet.has(value)
}
export function dataStatus(data: unknown, fallback: NodeStatus = 'idle'): NodeStatus {
  const v = dataValue(data, 'status')
  return isNodeStatus(v) ? v : fallback
}
function yString(map: YDocMap, key: string, fallback = ''): string {
  const value = map.get(key)
  return typeof value === 'string' ? value : fallback
}

function yOptionalString(map: YDocMap, key: string): string | undefined {
  const value = map.get(key)
  return typeof value === 'string' ? value : undefined
}

function yNumber(map: YDocMap, key: string, fallback = 0): number {
  const value = map.get(key)
  return typeof value === 'number' ? value : fallback
}

function yOptionalNumber(map: YDocMap, key: string): number | undefined {
  const value = map.get(key)
  return typeof value === 'number' ? value : undefined
}

export function activeTextEntryElement(doc?: Document): Element | null {
  const sourceDoc = doc ?? (typeof document !== 'undefined' ? document : null)
  const active = sourceDoc?.activeElement
  if (!active || active === sourceDoc?.body) return null

  const editable = active.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"]')
  if (!editable) return null

  if (editable instanceof HTMLInputElement || editable instanceof HTMLTextAreaElement) {
    if (editable.disabled || editable.readOnly) return null
  }
  if (editable instanceof HTMLSelectElement && editable.disabled) return null
  if (editable instanceof HTMLElement) {
    if (editable.getAttribute('aria-readonly') === 'true' || editable.getAttribute('aria-disabled') === 'true') {
      return null
    }

    const testId = editable.dataset.testid ?? ''
    const isChatComposer = testId === 'chat-input-editor' || testId === 'chat-composer-editor'
    const normalizedText = (editable.textContent ?? '').replace(/\u00a0/g, ' ').trim()
    if (isChatComposer && normalizedText === '') return null
  }

  return editable
}

export function isTextEntryActive(doc?: Document): boolean {
  return activeTextEntryElement(doc) !== null
}

function describeElementForLog(element: Element | null): Record<string, string> {
  if (!element) return {}
  const htmlElement = element instanceof HTMLElement ? element : null
  return {
    activeTag: element.tagName.toLowerCase(),
    activeRole: element.getAttribute('role') ?? '',
    activeTestId: htmlElement?.dataset.testid ?? '',
    activeAriaLabel: element.getAttribute('aria-label') ?? '',
  }
}

function compareTargetIndex(left: number | undefined, right: number | undefined): number {
  if (left === undefined && right === undefined) return 0
  if (left === undefined) return 1
  if (right === undefined) return -1
  return left - right
}

type TargetPortRef = {
  target: string
  targetHandle?: string
}

function yEdgeSourcePort(yedge: YDocMap): string | undefined {
  return yOptionalString(yedge, 'from_port')
}

function yEdgeTargetPort(yedge: YDocMap): string | undefined {
  return yOptionalString(yedge, 'to_port')
}

function rememberTargetPort(
  ports: TargetPortRef[],
  target: string,
  targetHandle: string | undefined,
): void {
  const exists = ports.some(
    (port) => port.target === target && port.targetHandle === targetHandle,
  )
  if (!exists) ports.push({ target, targetHandle })
}

function reindexTargetPortEdges(
  yEdges: YDocMapTable,
  target: string,
  targetHandle: string | undefined,
): void {
  const siblings: Array<{ edge: YDocMap; toIndex: number | undefined }> = []
  yEdges.forEach((yedge: YDocMap) => {
    const edgeTarget = yString(yedge, 'target')
    const edgeTargetHandle = yEdgeTargetPort(yedge)
    if (edgeTarget === target && edgeTargetHandle === targetHandle) {
      siblings.push({ edge: yedge, toIndex: yOptionalNumber(yedge, 'toIndex') })
    }
  })
  siblings
    .sort((left, right) => compareTargetIndex(left.toIndex, right.toIndex))
    .forEach((item, index) => item.edge.set('toIndex', index))
}

function isGeneratedBackendNodeType(backendType: string): backendType is BackendNodeType {
  return backendType in CANVAS_NODE_FIELD_TYPES
}

function validateTypedCanvasNode(ynode: YDocMap, backendType: string): void {
  if (!isGeneratedBackendNodeType(backendType)) return
  bindRawCanvasNode(ynode, backendType).readDataFields()
}

function normalizeReactFlowFieldValue(value: ReactFlowFieldValue): ReactFlowFieldValue {
  if (value === null || value === undefined) return value
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value
  if (value instanceof Y.Map || value instanceof Y.Array) return value
  if (typeof File !== 'undefined' && value instanceof File) return value
  if (typeof value === 'object') return value
  return String(value)
}

function normalizeNodeDataUpdates(updates: RawNodeDataUpdates): RawNodeDataUpdates {
  const normalized: RawNodeDataUpdates = {}
  for (const [key, value] of Object.entries(updates)) {
    normalized[key] = normalizeReactFlowFieldValue(value)
  }
  return normalized
}

function validateResyncNodeDataUpdates(
  feType: FeNodeType,
  backendType: BackendNodeType,
  updates: RawNodeDataUpdates,
): RawNodeDataUpdates {
  const normalizedUpdates = normalizeNodeDataUpdates(updates)
  const declaredFields = CANVAS_NODE_FIELD_TYPES[backendType]
  const generatedUpdates: RawNodeDataUpdates = {}
  for (const [key, value] of Object.entries(normalizedUpdates)) {
    if (Object.prototype.hasOwnProperty.call(declaredFields, key)) {
      generatedUpdates[key] = value
      continue
    }
    if (
      RESYNC_COMMON_DATA_FIELDS.has(key) &&
      value !== null &&
      value !== undefined &&
      typeof value !== 'string' &&
      typeof value !== 'number' &&
      typeof value !== 'boolean'
    ) {
      throw new TypeError(`${key} must be a JSON primitive for resync common field`)
    }
  }
  if (Object.keys(generatedUpdates).length === 0) return normalizedUpdates

  const probeDoc = new Y.Doc()
  const probeNode = new Y.Map<YDocValue>()
  probeNode.set('type', feType)
  probeDoc.getMap<Y.Map<YDocValue>>('nodes').set('probe-node', probeNode)
  const typedProbeNode = bindRawCanvasNode(probeNode, backendType)
  typedProbeNode.writeDataFields(generatedUpdates, { forceDesignerJsonFields: true })
  typedProbeNode.readDataFields()
  return normalizedUpdates
}

/**
 * Read node data from a Y.Map entry using per-field keys.
 *
 * RFC-0319: All non-structural keys are read as node data fields.
 * This includes schema-declared input/output fields and legacy state fields.
 * Data-field deserialization is schema-driven via generated YDoc readers.
 */
export function readNodeDataFromYMap(ynode: YDocMap): Record<string, unknown> {
  const feType = yString(ynode, 'type')
  const backendType = toBackendType(feType)
  validateTypedCanvasNode(ynode, backendType)
  const dataRaw = isGeneratedBackendNodeType(backendType)
    ? bindRawCanvasNode(ynode, backendType).readDataFields()
    : {}
  const statusVal = yOptionalString(ynode, 'status')
  statusLog('read nodeType=%s fieldCount=%d status=%s', feType, Object.keys(dataRaw).length, statusVal ?? 'undefined')

  return dataRaw
}

/** readDataFields throw text for a stored key absent from the generated schema. */
const UNDECLARED_FIELD_ERROR = 'is not declared in generated schema'

/**
 * Resilient wrapper used by the canvas rebuild/merge paths. These reads run
 * inside a useEffect / Yjs observer with NO React error boundary above them, so
 * an uncaught throw unmounts the entire app (blank canvas).
 *
 * We tolerate ONLY the schema-drift class: a stored key that is no longer
 * declared in the generated schema — a field removed without being added to
 * YDOC_RETIRED_DATA_KEYS (e.g. RFC-1072 `out_unified_audio`). For that case we
 * log loudly and degrade to empty data: the node still renders (so parent/edge
 * references stay valid) and any per-node render error is caught by
 * NodeErrorBoundary. Every OTHER error (type/invariant/validation violations,
 * programming errors) is rethrown so it still fails loudly per the coding
 * guidelines — silently converting those into fallback state would hide real
 * regressions. Do NOT fold this into readNodeDataFromYMap: useLibraryYDoc relies
 * on it throwing so it can run its own skip logic.
 */
export function readNodeDataFromYMapSafe(ynode: YDocMap, nodeId: string): Record<string, unknown> {
  try {
    return readNodeDataFromYMap(ynode)
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    if (!message.includes(UNDECLARED_FIELD_ERROR)) throw error
    const nodeType = yString(ynode, 'type', '<missing>')
    console.error(
      `[useYCanvas] node ${nodeId} (${nodeType}) has an undeclared persisted field; rendering with empty data — ${message}. Add the stale key to YDOC_RETIRED_DATA_KEYS.`,
    )
    return {}
  }
}

const DESIGNER_BACKEND_TYPES = new Set(['character_designer', 'location_designer', 'object_designer'])
const OUT_IMAGE_FIELD = 'out_image'

function isRecord(value: ReactFlowFieldValue): value is RawNodeDataUpdates {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function parseJsonString(value: ReactFlowFieldValue): ReactFlowFieldValue {
  if (typeof value !== 'string') return value
  try { return JSON.parse(value) } catch { return value }
}

function fieldValue(value: ReactFlowFieldValue, key: string): ReactFlowFieldValue {
  const parsed = parseJsonString(value)
  if (parsed instanceof Y.Map) return parsed.get(key)
  if (isRecord(parsed)) return parsed[key]
  return undefined
}

function toIndex(value: ReactFlowFieldValue): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return undefined
}

function nestedFieldValue(value: ReactFlowFieldValue, path: string[]): ReactFlowFieldValue {
  return path.reduce<ReactFlowFieldValue>((current, key) => fieldValue(current, key), value)
}

function applyDesignerPickedIndexPatches(
  updates: RawNodeDataUpdates,
  ynode: YDocMap,
  nodeId: string,
  backendType: string,
): void {
  if (!DESIGNER_BACKEND_TYPES.has(backendType) || !('out_image' in updates)) return
  const pickedIndex = toIndex(nestedFieldValue(updates.out_image, ['images', 'main', 'picked_index']))
  if (pickedIndex === undefined) return

  const asset = ynode.get(OUT_IMAGE_FIELD)
  const images = asset instanceof Y.Map ? asset.get('images') : undefined
  const main = images instanceof Y.Map ? images.get('main') : undefined
  if (!(main instanceof Y.Map)) {
    ydocWriteLog('designer picked_index patch skipped nodeId=%s field=out_image reason=missing-main-map', nodeId)
    return
  }

  if (main.get('picked_index') !== pickedIndex) {
    main.set('picked_index', pickedIndex)
    ydocWriteLog('write nodeId=%s key=out_image.images.main.picked_index value=%d', nodeId, pickedIndex)
  } else {
    ydocWriteLog('skip same-value nodeId=%s key=out_image.images.main.picked_index value=%d', nodeId, pickedIndex)
  }
}

/**
 * Node as stored in the Y document (backend-computed position included).
 */
export interface BackendNode {
  id: string
  type: string
  data: {
    status: string
    nodeType?: string
    [key: string]: ReactFlowFieldValue
  }
  position: { x: number; y: number }
}

// ---------------------------------------------------------------------------
// useYCanvas: Y doc → ReactFlow nodes/edges (single observer)
// ---------------------------------------------------------------------------

interface UseYCanvasOptions {
  ydoc: Y.Doc
  synced: boolean
  /** ReactFlow setNodes */
  setNodes: (nodes: ReactFlowCanvasNode[] | ((prev: ReactFlowCanvasNode[]) => ReactFlowCanvasNode[])) => void
  /** ReactFlow setEdges */
  setEdges: (edges: ReactFlowCanvasEdge[] | ((prev: ReactFlowCanvasEdge[]) => ReactFlowCanvasEdge[])) => void
  /** Ref to current ReactFlow nodes */
  nodesRef: React.MutableRefObject<ReactFlowCanvasNode[]>
  /** Ref to current ReactFlow edges */
  edgesRef: React.MutableRefObject<ReactFlowCanvasEdge[]>
  /** Whether backend is connected and Y doc is synced */
  isBackendConnected: boolean
  /** Ref to skip rebuild during undo/redo */
  isUndoRedo: React.MutableRefObject<boolean>
  /** ReactFlow instance ref for auto-focus */
  reactFlowInstance: React.MutableRefObject<ReactFlowInstance<ReactFlowCanvasNodeData, ReactFlowCanvasEdgeData> | null>
  /** When set, skip YDoc nodes whose type is not in this set */
  knownNodeTypes?: ReadonlySet<string>
  /** Selection ref — source of truth for which nodes are selected */
  selectedNodeIdsRef: React.RefObject<Set<string>>
  /** Current project ID — used to invalidate the cost-summary React Query
   *  entry when a non-director node terminalizes, so the Sparks & Usage
   *  modal's Node Breakdown reflects fresh data without a page refresh. */
  projectId?: string
  /** Current canvas ID — used by targeted node resyncs after out-of-band work
   *  updates the authoritative canvas state. */
  canvasId?: string
  /** Timestamp (ms epoch) of the user's most recent canvas interaction.
   *  Owned/reset by WorkspacePageV2 (scoped per workspace/canvas). Read live
   *  inside the focus observers to suppress AUTOMATIC auto-focus while the user
   *  is actively interacting. Explicit Director focus bypasses this. */
  lastInteractionTsRef: React.MutableRefObject<number>
}

interface UseYCanvasResult {
  /** True after the first rebuild has populated nodes into React state */
  ready: boolean
  /** Number of non-director nodes with status=running in YDoc */
  ydocRunningCount: number
  /** Number of non-director nodes with status=pending_execution in YDoc */
  ydocQueuedCount: number
  /**
   * True when the director node's YDoc status is `running`. Only meaningful
   * while `ydocStatusLive` is true — before the first rebuild it is a default,
   * and after a disconnect it is a memory.
   */
  ydocDirectorRunning: boolean
  /**
   * True when the YDoc-derived status values above reflect a LIVE, synced
   * document: the first rebuild has walked the nodes AND the transport is
   * still connected. `ready` alone is not enough — it is not cleared when
   * `synced` / `isBackendConnected` drop (the effect simply returns), so it
   * stays true while the values behind it go stale.
   */
  ydocStatusLive: boolean
  /** Write a node's position (and optionally tainted flag) back to the Y document */
  writeNodePosition: (nodeId: string, x: number, y: number, tainted?: boolean) => void
  /** Pre-create a node in the Y doc so backend reconciliation preserves its position */
  addNodeToYDoc: (nodeId: string, nodeType: string, label: string, x: number, y: number, data?: RawNodeDataUpdates, tainted?: boolean, parentId?: string) => void
  /** Remove a node from the Y doc (cleanup on deletion) */
  removeNodeFromYDoc: (nodeId: string) => void
  /** Patch per-field in_/out_ keys on a node's YDoc entry */
  writeNodeData: <T extends FeNodeType>(node: ReactFlowNodeRef<T>, updates: NodeDataUpdates<T>) => void
  /** Flush one downstream input from connected source edge refs via generated field writers */
  flushNodeInputFromEdgeRefs: (targetId: string, targetHandle: string, nodes: readonly YDocInputFlushNode[], edgeRefs: readonly YDocInputFlushEdgeRef[]) => YDocInputFlushResult | undefined
  /** Write measured dimensions back to YDoc so backend layout knows real sizes */
  writeNodeDimensions: (nodeId: string, width: number, height: number) => void
  /** Set or clear the top-level parentNode field on a ynode (sets the ReactFlow parentId) */
  writeNodeParent: (nodeId: string, parentId: string | null) => void
  /** Add an edge to YDoc */
  addEdgeToYDoc: (edge: { source: string; target: string; sourceHandle?: string; targetHandle?: string; toIndex?: number; fromIndex?: number; type?: string }) => void
  /** Remove an edge from YDoc by its ID */
  removeEdgeFromYDoc: (edgeId: string) => void
  /** Remove edges from YDoc matching source/target/ports */
  removeEdgesFromYDocByPorts: (edges: Array<{ source: string; target: string; sourceHandle?: string; targetHandle?: string }>) => void
}

/**
 * Stamp node.selected from the selection set ref. Used during YDoc rebuilds to
 * preserve the user's current selection without scanning prevNodes.
 */
function applySelection(
  nodes: ReactFlowCanvasNode[],
  selectedIdsRef: React.RefObject<Set<string>>,
): ReactFlowCanvasNode[] {
  const selectedIds = selectedIdsRef.current
  if (!selectedIds || selectedIds.size === 0) return nodes
  return nodes.map(n => selectedIds.has(n.id) ? { ...n, selected: true } : n)
}

export function useYCanvas({
  ydoc,
  synced,
  setNodes,
  setEdges,
  nodesRef,
  edgesRef,
  isBackendConnected,
  isUndoRedo,
  reactFlowInstance,
  knownNodeTypes,
  selectedNodeIdsRef,
  projectId,
  canvasId,
  lastInteractionTsRef,
}: UseYCanvasOptions): UseYCanvasResult {
  // True after the first rebuild has populated nodes — used to gate canvas visibility
  const [ready, setReady] = useState(false)
  // Running non-director node count from YDoc.
  const [ydocRunningCount, setYdocRunningCount] = useState(0)
  // Queued (pending_execution) non-director node count from YDoc
  const [ydocQueuedCount, setYdocQueuedCount] = useState(0)
  // Whether the director node itself is running. Read from the same all-node
  // walk that produces the counts above; the chat panel uses it to tell
  // "the Director is thinking" apart from "a worker node is running" after a
  // cold load, when it has no local memory of either.
  const [ydocDirectorRunning, setYdocDirectorRunning] = useState(false)
  const ydocTaskCountsRef = useRef({ running: 0, queued: 0 })

  // Cost-summary cache invalidation — trailing-edge debounce so a batch
  // of N panels terminalizing back-to-back collapses to ONE refetch
  // after StateManager has had a chance to commit all of them.
  const queryClient = useQueryClient()
  const costInvalidateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const costInvalidateBatchedRef = useRef(0)
  const sparksInvalidateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const sparksInvalidateBatchedRef = useRef(0)
  const sparksInvalidateNodeIdsRef = useRef<Set<string>>(new Set())
  const nodeResyncSeqRef = useRef(0)
  const nodeResyncSeqByNodeRef = useRef<Map<string, number>>(new Map())

  // Keep refs to Y maps for write helpers
  const yNodesRef = useRef<YDocMapTable | null>(null)
  const readyYDocRef = useRef<Y.Doc | null>(null)
  const builtYDocRef = useRef<Y.Doc | null>(null)
  const activeCanvasScopeRef = useRef<ActiveCanvasScope>({
    ydoc,
    projectId,
    canvasId: canvasId ?? 'agent',
  })

  useLayoutEffect(() => {
    activeCanvasScopeRef.current = {
      ydoc,
      projectId,
      canvasId: canvasId ?? 'agent',
    }
  }, [ydoc, projectId, canvasId])

  // Track whether this is the first build (edges deferred by one frame)
  const isInitialBuild = useRef(true)

  // Track previously running nodes for auto-focus
  const prevRunningIdsRef = useRef<Set<string>>(new Set())

  // Debounce auto-focus: accumulate newly running nodes over a window before fitView
  const autoFocusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingFocusNodesRef = useRef<ReactFlowCanvasNode[]>([])

  // Explicit focus intents (from backend focus_node tool) take priority over auto-focus
  const lastFocusIntentTs = useRef(0)

  useEffect(() => {
    if (readyYDocRef.current !== ydoc) {
      readyYDocRef.current = ydoc
      setReady(false)
    }
    if (!synced || !isBackendConnected) {
      lifecycleLog('effect skip: synced=%s isBackendConnected=%s', synced, isBackendConnected)
      return
    }
    lifecycleLog('effect run: synced=%s isBackendConnected=%s', synced, isBackendConnected)

    const isFirstBuildForYDoc = builtYDocRef.current !== ydoc
    isInitialBuild.current = true

    const yNodes = ydoc.getMap<YDocMap>('nodes')
    const yEdges = ydoc.getMap<YDocMap>('edges')
    yNodesRef.current = yNodes

    const readNodesFromYDoc = (_reason = 'readNodesFromYDoc'): ReactFlowCanvasNode[] => {
      const nodes: ReactFlowCanvasNode[] = []
      yNodes.forEach((ynode: YDocMap, nodeId: string) => {
        const nodeType = yString(ynode, 'type')

        // Filter hidden, structural, retired, and caller-unknown types before typed reads.
        if (!isRenderableCanvasNodeType(nodeType, knownNodeTypes)) {
          if (!CANVAS_HIDDEN_FE_TYPES.has(nodeType)) {
            lifecycleLog('skip unsupported ReactFlow node type=%s nodeId=%s', nodeType, nodeId)
          }
          return
        }

        // RFC-0319: read per-field keys. Guarded: a stored node whose data no
        // longer matches the schema degrades to empty data instead of blanking
        // the whole canvas. See readNodeDataFromYMapSafe.
        const dataRaw = readNodeDataFromYMapSafe(ynode, nodeId)
        const parentId = yOptionalString(ynode, 'parentNode')

        // Group nodes need explicit dimensions so ReactFlow sizes the container.
        // For nodeGroup the width is component-driven (GroupNode computes
        // containerWidth from children layout); for webtoonGroup and
        // shortVideoGroup the width is taken directly from YDoc (the FE
        // measures and writes the rendered DOM width via writeNodeDimensions).
        const isGroup = nodeType === 'webtoonGroup' || nodeType === 'shortVideoGroup' || nodeType === 'nodeGroup'
        const isYDocSizedGroup = nodeType === 'webtoonGroup' || nodeType === 'shortVideoGroup'
        const collapsed = nodeType === 'nodeGroup'
          ? dataRaw.in_collapsible === true && dataRaw.in_collapsed === true
          : dataRaw.in_collapsed === true

        // Only set parentId if the parent exists in YDoc AND will be rendered.
        // A parent that exists in YDoc but is filtered out by knownNodeTypes
        // (e.g. legacy "group" after rename to "nodeGroup") would crash
        // ReactFlow with "Parent node ... not found".
        const parentExists = parentId ? (() => {
          const parentYNode = yNodes.get(parentId)
          if (!parentYNode) return false
          const parentType = yString(parentYNode, 'type')
          return isRenderableCanvasNodeType(parentType, knownNodeTypes)
        })() : false

        const posX = yNumber(ynode, 'x')
        const posY = yNumber(ynode, 'y')

        // Diagnostic logging for designer nodes — traces YDoc position data
        if (['characterDesigner', 'locationDesigner', 'objectDesigner'].includes(nodeType)) {
          layoutLog('[layout] readNodesFromYDoc: %s type=%s x=%d y=%d ydoc_w=%s ydoc_h=%s',
            nodeId, nodeType, posX, posY, ynode.get('width'), ynode.get('height'))
        }

        const node = {
          id: nodeId,
          type: nodeType,
          position: { x: posX, y: posY },
          data: {
            ...dataRaw,
            status: yString(ynode, 'status', 'idle'),
            label: yString(ynode, 'label', nodeId),
          } as ReactFlowCanvasNodeData,
          ...(parentExists ? { parentId } : {}),
          ...(isGroup ? {
            style: {
              width: isYDocSizedGroup ? yOptionalNumber(ynode, 'width') : undefined,
              height: collapsed ? 48 : yOptionalNumber(ynode, 'height'),
              padding: 0,
              backgroundColor: 'transparent',
              border: 'none',
            },
          } : {}),
        } as ReactFlowCanvasNode

        // Stash the authoritative expanded height so collapse toggle can restore it
        if (isGroup) {
          const ydocHeight = yOptionalNumber(ynode, 'height')
          if (ydocHeight) {
            node.data._expandedHeight = ydocHeight
          }
        }

        // Child nodes inside a shortVideoGroup should be non-draggable (auto-layout)
        if (parentExists) {
          const parentYNode = yNodes.get(parentId!)
          if (parentYNode) {
            const parentType = yOptionalString(parentYNode, 'type')

            // Lock panels in horizontal layout groups (Figma-like auto-layout)
            if (parentType === 'shortVideoGroup') {
              node.draggable = false
            }
          }
        }

        nodes.push(node)
      })
      // deriveGroupedNodeLayout now enforces parent-before-child array order
      // (ReactFlow requires parents before children) via its internal
      // sortParentsBeforeChildren pass — single source of truth, no separate
      // sort needed here.
      return deriveGroupedNodeLayout(nodes)
    }

    // Build child→collapsedGroup mapping for edge remapping
    const getCollapsedGroupMap = (): Map<string, string> => {
      const childToGroup = new Map<string, string>()
      yNodes.forEach((ynode: YDocMap, nodeId: string) => {
        const parentId = yOptionalString(ynode, 'parentNode')
        if (!parentId) return
        const parentYNode = yNodes.get(parentId)
        if (!parentYNode) return
        const parentBackendType = schemaBackendType(yString(parentYNode, 'type'))
        if (parentBackendType == null) return
        // Guarded: a schema-drifted parent group degrades to {} (not collapsed)
        // instead of blanking the canvas via the edge-rebuild path.
        const parentData = isGeneratedBackendNodeType(parentBackendType)
          ? readNodeDataFromYMapSafe(parentYNode, parentId)
          : {}
        const parentCanCollapse = parentBackendType !== 'node_group'
          || parentData.in_collapsible === true
        if (parentCanCollapse && parentData.in_collapsed === true) {
          childToGroup.set(nodeId, parentId)
        }
      })
      return childToGroup
    }

    const readEdgesFromYDoc = (): ReactFlowCanvasEdge[] => {
      const collapsedMap = getCollapsedGroupMap()
      const edges: ReactFlowCanvasEdge[] = []
      const seen = new Set<string>()
      yEdges.forEach((yedge: YDocMap, edgeId: string) => {
        let source = yString(yedge, 'source')
        let target = yString(yedge, 'target')
        const storedTarget = target
        const storedType = yString(yedge, 'type', 'default')
        const referencePairKind = referencePairKindFromEdgeType(storedType)
        const type = referencePairKind === undefined ? storedType : 'default'
        let sourceHandle = yEdgeSourcePort(yedge)
        let targetHandle = yEdgeTargetPort(yedge)

        // Remap edges involving children of collapsed groups
        if (collapsedMap.has(source)) {
          source = collapsedMap.get(source)!
          sourceHandle = undefined
        }
        if (collapsedMap.has(target)) {
          target = collapsedMap.get(target)!
          targetHandle = undefined
        }
        // Skip self-loops (both ends remapped to same group)
        if (source === target) return
        // Skip edges that were authored directly to group nodes — parent-child
        // membership is via parentId, not edges. Keep ordinary inbound edges
        // whose child target was only remapped to a collapsed group proxy.
        const targetYNode = yNodes.get(storedTarget)
        if (targetYNode) {
          const targetType = yOptionalString(targetYNode, 'type')
          if (targetType === 'nodeGroup' || targetType === 'shortVideoGroup' || targetType === 'webtoonGroup') return
        }
        // Only generic nodeGroup renders the new target proxy handle. Preserve
        // the established specialized-group behavior until those components
        // explicitly opt into inbound edge proxies.
        if (target !== storedTarget) {
          const proxyTargetYNode = yNodes.get(target)
          if (!proxyTargetYNode || yOptionalString(proxyTargetYNode, 'type') !== 'nodeGroup') return
        }
        const toIndex = yOptionalNumber(yedge, 'toIndex')
        const fromIndex = yOptionalNumber(yedge, 'fromIndex')
        // Deduplicate exact rendered edges after collapsed-group remapping.
        const dedup = [
          source,
          sourceHandle ?? '',
          target,
          targetHandle ?? '',
          fromIndex ?? '',
          toIndex ?? '',
        ].join('→')
        if (seen.has(dedup)) return
        seen.add(dedup)

        const edge: ReactFlowCanvasEdge = { id: edgeId, source, target, type }
        if (sourceHandle) edge.sourceHandle = sourceHandle
        if (targetHandle) edge.targetHandle = targetHandle
        if (toIndex !== undefined) {
          edge.data = { ...edge.data, toIndex }
        }
        if (fromIndex !== undefined) {
          edge.data = { ...edge.data, fromIndex }
        }
        if (referencePairKind !== undefined) {
          edge.data = { ...edge.data, referencePairKind }
        }
        edges.push(edge)
      })
      return edges
    }

    const yClientID = ydoc.clientID

    const rebuild = (events?: YDocEvent[], source?: 'nodes' | 'edges'): void => {
      const origin = events && events.length > 0 ? events[0].transaction.origin : 'initial'
      // Node ids whose YDoc `label` key changed in this event batch (debug-only;
      // empty when the namespace is off, so the loops below are no-ops).
      const labelChangedIds: string[] = []
      if (labelApplyLog.enabled && events && source === 'nodes') {
        for (const e of events) {
          const labelChange = e.changes?.keys?.get('label')
          if (!labelChange || !(e.target instanceof Y.Map)) continue
          const ymap: YDocMap = e.target
          const nodeId = [...yNodes.entries()].find(([, v]) => v === ymap)?.[0] ?? '??'
          labelChangedIds.push(nodeId)
          labelApplyLog({
            event: 'label-ydoc-event',
            nodeId,
            action: labelChange.action,
            oldLabel: String(labelChange.oldValue ?? ''),
            newLabel: String(ymap.get('label') ?? ''),
            origin: String(origin),
            batchEvents: events.length,
          })
        }
      }
      // During undo/redo, skip — the undo handler owns node state
      if (isUndoRedo.current) {
        if (labelChangedIds.length > 0) labelApplyLog({ event: 'label-rebuild-skipped', reason: 'undo-redo', nodeIds: labelChangedIds.join(',') })
        return
      }
      const groupCollapseChanged = events?.some((event) =>
        event.target instanceof Y.Map
        && event.changes?.keys?.has('in_collapsed')
        && yOptionalString(event.target, 'type') === 'nodeGroup',
      ) ?? false

      // Skip rebuild for our own frontend writes (e.g. config field edits),
      // EXCEPT when the transaction structurally removes a node from the
      // yNodes Y.Map. Deletion must be YDoc → ReactFlow (single source of
      // truth): rebuild() is the sole writer of React node state on delete, so
      // we let structural removals drive a full rebuild instead of early-
      // returning. Non-structural frontend writes still early-return.
      if (events && events.length > 0 && events[0].transaction.origin === 'frontend') {
        const removedNodeIds: string[] = []
        for (const e of events) {
          if (e.target !== yNodes) continue
          const keys = e.changes?.keys
          if (!keys) continue
          for (const [key, change] of keys) {
            if (change.action === 'delete') removedNodeIds.push(key)
          }
        }
        const hasStructuralNodeRemoval = removedNodeIds.length > 0
        // A local group collapse is also structural from ReactFlow's point of
        // view: children become hidden and their visible edge endpoints move to
        // the group proxy handles. Let that one data write continue through the
        // normal node/edge rebuild path.
        if (!hasStructuralNodeRemoval && !groupCollapseChanged) {
          if (labelChangedIds.length > 0) labelApplyLog({ event: 'label-rebuild-skipped', reason: 'frontend-origin', nodeIds: labelChangedIds.join(',') })
          return
        }
        if (hasStructuralNodeRemoval) {
          log('rebuild → STRUCTURAL-REMOVAL: frontend node delete, full rebuild from YDoc (yNodes.size=%d)', yNodes.size)
          nodeDeleteRebuildLog({
            event: 'rebuild-structural-removal',
            removedNodeIds,
            removedCount: removedNodeIds.length,
            yNodesSize: yNodes.size,
          })
        }
      }

      // --- Edge-only fast path ---
      // When only edges changed (add/remove/reorder), update edges without
      // touching nodes. Calling setNodes with new objects forces ReactFlow to
      // re-measure node dimensions, which briefly un-renders all edges.
      if (source === 'edges' && !isInitialBuild.current) {
        log('rebuild → EDGE-ONLY fast path (client=%d origin=%s)', yClientID, origin)
        setEdges(() => {
          const freshEdges = readEdgesFromYDoc()
          edgesRef.current = freshEdges
          return freshEdges
        })
        return
      }

      // --- Node data-only fast path (status/label/in_*/out_*) ---
      // Update React node data in place without rebuilding from scratch.
      // RFC-0319: per-field keys (in_*/out_*) are also non-structural data updates.
      const isNonStructuralKey = (key: string): boolean =>
        !YDOC_STRUCTURAL_KEYS.has(key) ||
        key === 'status' || key === 'label'

      // RFC-0785 (Fuzzy Moseying Hamster) — array-backed YDoc fields like
      // `out_image.history` emit Yjs deep events whose target is the nested
      // Y.Array, not the owning node Y.Map. Previously such events fell out
      // of the data-only branch and triggered a full rebuild, defeating the
      // sibling identity-preservation fix below for any out_* / in_* field
      // backed by an array or nested map. Walk the parent chain up to one
      // of yNodes' direct values to recognise these as data-only events.
      // Cap depth defensively at 8 — in_*/out_* nested structures are at
      // most a few levels deep in practice.
      const resolveEventNodeId = (e: YDocEvent): string | undefined => {
        let cursor: Y.AbstractType<unknown> | null =
          e.target as Y.AbstractType<unknown> | null
        for (let depth = 0; cursor && depth < 8; depth++) {
          for (const [id, ymap] of yNodes.entries()) {
            if (ymap === cursor) return id
          }
          cursor = (cursor as { parent?: Y.AbstractType<unknown> | null }).parent ?? null
        }
        return undefined
      }

      const isTargetANode = (target: unknown): boolean => {
        if (!(target instanceof Y.Map)) return false
        for (const ymap of yNodes.values()) {
          if (ymap === target) return true
        }
        return false
      }

      const isDataOnlyEvent = (e: YDocEvent): boolean => {
        // Case 1: direct node Y.Map mutation (status / label / per-field
        // primitive). All changed keys must be non-structural and 'update'
        // (NOT 'add'/'delete' — those mean schema-shape change).
        if (isTargetANode(e.target)) {
          const keys: Map<string, { action: string }> | undefined = e.changes?.keys
          if (!keys || keys.size === 0) return false
          for (const [key, change] of keys) {
            if (!isNonStructuralKey(key) || change.action !== 'update') return false
          }
          return true
        }
        // Case 2: nested mutation inside a node — content change on a
        // wireFormat: "map" / "array" field (e.g. out_image.history.push).
        // These are by construction non-structural; the owning node id is
        // what matters and is resolved via the parent walk.
        return resolveEventNodeId(e) !== undefined
      }
      const isDataOnlyUpdate = events && events.length > 0 && nodesRef.current.length > 0 && events.every(isDataOnlyEvent)
      const layoutSensitiveGroupIds = new Set<string>()
      if (events) {
        for (const e of events) {
          if (!(e.target instanceof Y.Map)) continue
          const keys: Map<string, { action: string }> | undefined = e.changes?.keys
          if (!keys || keys.size === 0) continue
          const changedNodeId = [...yNodes.entries()].find(([, v]) => v === e.target)?.[0]
          if (!changedNodeId) continue
          for (const [key] of keys) {
            if (key === 'in_child_node_ids' || key === 'in_layout_mode' || key === 'in_collapsed') {
              layoutSensitiveGroupIds.add(changedNodeId)
            }
          }
        }
      }

      // Debug: log status changes on any node
      if (events) {
        for (const e of events) {
          if (!(e.target instanceof Y.Map)) continue
          const keys: Map<string, { action: string; oldValue?: YDocValue }> | undefined = e.changes?.keys
          if (!keys) continue
          for (const [key, change] of keys) {
            if (key === 'status') {
              const ymap: YDocMap = e.target
              const nodeId = [...yNodes.entries()].find(([, v]) => v === ymap)?.[0] ?? '??'
              const newVal = ymap.get('status')
              const nodeType = yOptionalString(ymap, 'type')
              log('statusChange node=%s status: %s → %s (client=%d origin=%s)', nodeId, String(change.oldValue), String(newVal), yClientID, String(origin))
              statusLog('ydoc-change nodeId=%s nodeType=%s old=%s new=%s origin=%s', nodeId, nodeType ?? '??', String(change.oldValue), String(newVal), String(origin))
            }
          }
        }
      }

      // Count active non-director nodes across ALL YDoc nodes (including canvas-hidden types).
      // Must run on every update (data-only AND full rebuild) so the task banner stays current.
      {
        let runCount = 0
        let queuedCount = 0
        let directorRunning = false
        const runningIds: string[] = []
        const queuedIds: string[] = []
        yNodes.forEach((ynode: YDocMap, nodeId: string) => {
          const st = yOptionalString(ynode, 'status')
          const tp = yOptionalString(ynode, 'type')
          if (tp === 'director') {
            // Not a task-card node, but the chat panel needs it to tell
            // Director work apart from worker work on a cold load.
            if (st === 'running') directorRunning = true
            return
          }
          if (st === 'running') {
            runCount++
            runningIds.push(`${nodeId}(${tp})`)
          } else if (st === 'pending_execution') {
            queuedCount++
            queuedIds.push(`${nodeId}(${tp})`)
          }
        })
        log(
          'runCount running=%d queued=%d %o',
          runCount,
          queuedCount,
          runningIds.length > 0 ? runningIds : '(none)',
        )
        const previous = ydocTaskCountsRef.current
        if (
          previous.running !== runCount ||
          previous.queued !== queuedCount
        ) {
          ydocTaskCountsRef.current = {
            running: runCount,
            queued: queuedCount,
          }
          chatTaskStatusLog({
            event: 'chat-task-counts-updated',
            trace_id: `chat-task-status:${projectId ?? ''}`,
            projectId: projectId ?? '',
            runningCount: runCount,
            queuedCount,
            runningNodeIds: runningIds.slice(0, 10).join(','),
            queuedNodeIds: queuedIds.slice(0, 10).join(','),
          })
        }
        setYdocRunningCount(runCount)
        setYdocQueuedCount(queuedCount)
        setYdocDirectorRunning(directorRunning)
      }

      if (isDataOnlyUpdate) {
        // Derive the set of node ids whose YDoc state actually changed in this
        // batch of events. Used to skip the data-merge wrapper rebuild for
        // siblings whose data did not change — which previously caused every
        // React.memo-wrapped ReactFlow node to re-render and, with
        // `renderOnlyVisibleElements: true`, briefly unmount sibling
        // <img>/<video> DOM during another node's generation lifecycle.
        //
        // resolveEventNodeId (defined above) covers BOTH the per-node Y.Map
        // case (status/label/in_* primitive writes) AND nested Y.Map/Y.Array
        // mutations inside `wireFormat: "map" | "array"` fields (e.g.
        // `out_image.history.push(...)`). Any event we cannot resolve to a
        // node id is counted in `unresolvedEventCount` so the safety fallback
        // (rebuild-all) below is observable in the NAD log.
        const changedNodeIds = new Set<string>()
        let unresolvedEventCount = 0
        if (events) {
          for (const e of events) {
            const nodeId = resolveEventNodeId(e)
            if (nodeId) {
              changedNodeIds.add(nodeId)
            } else {
              unresolvedEventCount++
            }
          }
        }

        log('rebuild → DATA-ONLY fast path (client=%d origin=%s)', yClientID, origin)
        // Use functional updater to avoid race with useEffect(() => nodesRef.current = nodes).
        // The useEffect can clobber nodesRef with stale React state between renders;
        // functional updaters always receive React's latest pending state.
        setNodes(prevNodes => {
          // Compare against visible YDoc nodes only. Hidden types such as
          // director live in YDoc but are never rendered.
          let visibleYNodeCount = 0
          yNodes.forEach((_ynode: YDocMap, _nodeId: string) => {
            const nt = yString(_ynode, 'type')
            if (!CANVAS_HIDDEN_FE_TYPES.has(nt) && (!knownNodeTypes || knownNodeTypes.has(nt))) {
              visibleYNodeCount++
            }
          })
          if (prevNodes.length === visibleYNodeCount) {
            log('rebuild DATA-ONLY: count match (%d), merging in-place', prevNodes.length)
            // Safety fallback: if event→node id resolution missed *every*
            // event (e.g. an observeDeep event whose target's parent chain
            // does not include a node map — a shape we don't yet know how to
            // walk), do NOT silently drop the update. Fall back to the
            // unfixed rebuild-all behavior so we never regress correctness
            // versus today's code. `unresolvedEventCount` and the
            // `nestedMapMiss=` count in the NAD log below make any regression
            // immediately observable.
            const useIdentityPreservation = changedNodeIds.size > 0
            let rebuiltCount = 0
            // First pass: merge data from YDoc, preserving identity for nodes
            // whose YDoc state did not change in this event batch.
            const updatedNodes = prevNodes.map(rfNode => {
              if (useIdentityPreservation && !changedNodeIds.has(rfNode.id)) {
                return rfNode
              }
              const ynode = yNodes.get(rfNode.id)
              if (!ynode) return rfNode

              // RFC-0319: read per-field keys with data_json fallback. Guarded:
              // schema-drifted data degrades to {} (keeping prior data on merge)
              // instead of crashing the whole canvas.
              const dataRaw = readNodeDataFromYMapSafe(ynode, rfNode.id)

              const newStatus = yString(ynode, 'status', 'idle')
              const newLabel = yString(ynode, 'label', rfNode.id)

              rebuiltCount++
              const merged: ReactFlowCanvasNode = {
                ...rfNode,
                data: {
                  ...rfNode.data,
                  ...dataRaw,
                  status: newStatus,
                  label: newLabel,
                } as ReactFlowCanvasNodeData,
              }

              return merged
            })
            log(
              'rebuild DATA-ONLY: merged total=%d changed=%d kept=%d nestedMapMiss=%d',
              prevNodes.length,
              changedNodeIds.size,
              prevNodes.length - rebuiltCount,
              unresolvedEventCount,
            )

            // Second pass: sync child hidden state with parent collapse
            const groupCollapseMap = new Map<string, boolean>()
            for (const n of updatedNodes) {
              if (n.type === 'webtoonGroup' || n.type === 'nodeGroup') {
                const collapsed = n.type === 'nodeGroup'
                  ? dataBool(n.data, 'in_collapsible') === true
                    && dataBool(n.data, 'in_collapsed') === true
                  : dataBool(n.data, 'in_collapsed') === true
                groupCollapseMap.set(n.id, collapsed)
              }
            }
            const groupLayoutNodes = layoutSensitiveGroupIds.size > 0
              ? deriveGroupedNodeLayout(updatedNodes, layoutSensitiveGroupIds)
              : updatedNodes
            const finalNodes = groupLayoutNodes.map(n => {
              if (n.parentId && groupCollapseMap.has(n.parentId)) {
                const parentCollapsed = groupCollapseMap.get(n.parentId)!
                if (n.hidden !== parentCollapsed) {
                  return { ...n, hidden: parentCollapsed }
                }
              }
              return n
            })

            for (const labelNodeId of labelChangedIds) {
              const rendered = finalNodes.find(n => n.id === labelNodeId)
              labelApplyLog({
                event: 'label-applied',
                path: 'data-only-merge',
                nodeId: labelNodeId,
                inPrevNodes: prevNodes.some(n => n.id === labelNodeId),
                inChangedNodeIds: changedNodeIds.has(labelNodeId),
                renderedLabel: rendered ? String(rendered.data.label ?? '') : '(absent)',
              })
            }
            nodesRef.current = finalNodes
            return finalNodes
          }

          // Mismatch: prevNodes is stale — rebuild from YDoc so the update
          // is not silently dropped.
          log('rebuild DATA-ONLY: COUNT MISMATCH prevNodes=%d visibleYNodes=%d (total=%d) — falling back to full rebuild', prevNodes.length, visibleYNodeCount, yNodes.size)
          const rebuilt = applySelection(readNodesFromYDoc('data-only-count-mismatch'), selectedNodeIdsRef)
          for (const labelNodeId of labelChangedIds) {
            const rendered = rebuilt.find(n => n.id === labelNodeId)
            labelApplyLog({
              event: 'label-applied',
              path: 'data-only-count-mismatch',
              nodeId: labelNodeId,
              renderedLabel: rendered ? String(rendered.data.label ?? '') : '(absent)',
            })
          }
          nodesRef.current = rebuilt
          return rebuilt
        })

        // Collapsing a group changes the visible endpoints of every edge touching
        // one of its children. Rebuild from the unchanged YDoc edge records so
        // local toggles and remote collaborator updates both proxy those edges to
        // the compact group node (and restore them again on expand).
        if (groupCollapseChanged) {
          setEdges(() => {
            const freshEdges = readEdgesFromYDoc()
            edgesRef.current = freshEdges
            return freshEdges
          })
        }

        _autoFocus(nodesRef.current)
        return
      }

      // --- Full rebuild path: structural node changes (add/remove nodes) ---
      // Use functional updaters that re-read YDoc at React flush time. When YDoc
      // fires rapid add/remove events, React queues each setNodes() call. Direct
      // calls like setNodes(snapshot) freeze state at event time, but by the time
      // React processes the queue earlier snapshots overwrite later ones — causing
      // the "oscillating node count" bug. Functional updaters always read the
      // latest YDoc state when React actually applies the update.

      if (!isInitialBuild.current) {
        log('rebuild → FULL REBUILD (structural change), yNodes.size=%d (client=%d origin=%s)', yNodes.size, yClientID, origin)
        setNodes((_prevNodes) => {
          const freshNodes = readNodesFromYDoc('full-rebuild')
          for (const labelNodeId of labelChangedIds) {
            const rendered = freshNodes.find(n => n.id === labelNodeId)
            labelApplyLog({
              event: 'label-applied',
              path: 'full-rebuild',
              nodeId: labelNodeId,
              renderedLabel: rendered ? String(rendered.data.label ?? '') : '(absent)',
            })
          }
          lifecycleLog('full rebuild: freshNodes=%d prevNodes=%d origin=%s', freshNodes.length, _prevNodes.length, origin)

          if (freshNodes.length === 0 && isInitialBuild.current) return _prevNodes
          const result = applySelection(freshNodes, selectedNodeIdsRef)

          nodesRef.current = result
          return result
        })
        setEdges(() => {
          const freshEdges = readEdgesFromYDoc()
          edgesRef.current = freshEdges
          return freshEdges
        })
        setReady(true)
      } else {
        isInitialBuild.current = false
        setNodes((prevNodes) => {
          const freshNodes = readNodesFromYDoc('initial-build')
          lifecycleLog(
            'initial build: freshNodes=%d prevNodes=%d firstYDocBuild=%s',
            freshNodes.length,
            prevNodes.length,
            isFirstBuildForYDoc,
          )
          nodesRef.current = freshNodes
          return freshNodes
        })

        builtYDocRef.current = ydoc
        if (!isFirstBuildForYDoc) {
          // A reconnect reuses the same Y.Doc. Publishing an empty edge array
          // here makes every edge consumer observe false disconnections for one
          // frame; the Video Editor then destructively reconciles its timeline.
          setEdges(() => {
            const freshEdges = readEdgesFromYDoc()
            edgesRef.current = freshEdges
            return freshEdges
          })
          setReady(true)
        } else {
          // On the first build, defer edges until ReactFlow has mounted their
          // endpoint nodes and handles.
          setEdges([])
          requestAnimationFrame(() => {
            if (builtYDocRef.current !== ydoc) return
            setEdges(() => {
              const freshEdges = readEdgesFromYDoc()
              edgesRef.current = freshEdges
              return freshEdges
            })
            setReady(true)
          })
        }
      }

      _autoFocus(readNodesFromYDoc('auto-focus-after-rebuild'))
    }

    const _autoFocus = (nodes: ReactFlowCanvasNode[]) => {
      const runningNodes = nodes.filter(n => {
        const status = typeof n.data?.status === 'string' ? n.data.status : undefined
        return status === 'running'
      })
      const currentRunningIds = new Set(runningNodes.map(n => n.id))
      const newlyRunning = runningNodes.filter(n => !prevRunningIdsRef.current.has(n.id))
      prevRunningIdsRef.current = currentRunningIds
      const focusCandidates = newlyRunning.filter(n => !isDepthVideoExecutionNode(n))
      const depthNodes = newlyRunning.filter(isDepthVideoExecutionNode)
      if (depthNodes.length > 0) {
        depthBatchLayoutLog({
          event: 'automatic-focus-suppressed',
          trace_id: `depth-batch-layout:${projectId ?? ''}`,
          project_id: projectId ?? '',
          trigger: 'running-status',
          reason: 'depth-execution-is-not-navigation',
          node_ids: depthNodes.map(n => n.id).join(','),
          node_count: depthNodes.length,
        })
      }

      if (focusCandidates.length === 0 || !reactFlowInstance.current) return

      const activeEditor = activeTextEntryElement()
      if (activeEditor) {
        viewportFocusLog({
          event: 'viewport-autofocus-suppressed-during-text-entry',
          trace_id: `canvas-viewport-focus:${projectId ?? ''}`,
          projectId: projectId ?? '',
          reason: 'active-text-entry',
          nodeIds: newlyRunning.map(n => n.id).join(','),
          ...describeElementForLog(activeEditor),
        })
        return
      }

      // Explicit focus_node intents take priority — skip auto-focus if one was
      // received recently so we don't override the user-facing pan animation.
      if (Date.now() - lastFocusIntentTs.current < 2000) return

      // Suppress automatic auto-focus while the user is actively interacting
      // with the canvas (RFC: suppress auto-focus during canvas interaction).
      // Checked here AND again at fire time below, because a user can interact
      // during the 500ms debounce window.
      const sinceInteractionMs = Date.now() - lastInteractionTsRef.current
      if (sinceInteractionMs < CANVAS_AUTOFOCUS_COOLDOWN_MS) {
        autofocusLog({
          event: 'autofocus-skip',
          path: 'running',
          reason: 'recent-interaction',
          sinceMs: sinceInteractionMs,
          nodeIds: newlyRunning.map(n => n.id).join(','),
        })
        return
      }

      // Accumulate newly running nodes and debounce the fitView call.
      // During batch generation (e.g. 9 storyboard panels), this prevents
      // the viewport from jumping to each panel individually.
      pendingFocusNodesRef.current.push(...focusCandidates)

      if (autoFocusTimerRef.current) {
        clearTimeout(autoFocusTimerRef.current)
      }

      autoFocusTimerRef.current = setTimeout(() => {
        const pending = pendingFocusNodesRef.current
        pendingFocusNodesRef.current = []
        autoFocusTimerRef.current = null

        if (pending.length > 0 && reactFlowInstance.current) {
          const activeEditor = activeTextEntryElement()
          if (activeEditor) {
            viewportFocusLog({
              event: 'viewport-autofocus-timer-suppressed-during-text-entry',
              trace_id: `canvas-viewport-focus:${projectId ?? ''}`,
              projectId: projectId ?? '',
              reason: 'active-text-entry',
              nodeIds: pending.map(n => n.id).join(','),
              ...describeElementForLog(activeEditor),
            })
            return
          }
          // Re-check the interaction cooldown at fire time: the user may have
          // interacted during the 500ms debounce window above.
          const sinceFireMs = Date.now() - lastInteractionTsRef.current
          if (sinceFireMs < CANVAS_AUTOFOCUS_COOLDOWN_MS) {
            autofocusLog({
              event: 'autofocus-skip-at-fire',
              path: 'running',
              reason: 'recent-interaction',
              sinceMs: sinceFireMs,
              nodeIds: pending.map(n => n.id).join(','),
            })
            return
          }
          autofocusLog({
            event: 'autofocus-fired',
            path: 'running',
            nodeIds: pending.map(n => n.id).join(','),
          })
          reactFlowInstance.current.fitView({
            nodes: pending,
            padding: 0.15,
            duration: 400,
            maxZoom: 1,
            minZoom: 0.3,
          })
        }
      }, 500)
    }

    // Initial build + observe both maps with source tags
    rebuild()
    const onNodesChange = (events: YDocEvent[]) => rebuild(events, 'nodes')
    const onEdgesChange = (events: YDocEvent[]) => rebuild(events, 'edges')
    yNodes.observeDeep(onNodesChange)
    yEdges.observeDeep(onEdgesChange)

    // --- Focus intent observer ---
    const yFocus = ydoc.getMap('focus')
    const scheduleFitView = (nodeId: string, attempt: number) => {
      const target = nodesRef.current.find(n => n.id === nodeId)
      if (!target) {
        if (attempt < 3) {
          log('focus: node %s not in React state yet, retry %d/3', nodeId, attempt + 1)
          setTimeout(() => scheduleFitView(nodeId, attempt + 1), 500)
        } else {
          log('focus: node %s not found after 3 retries, giving up', nodeId)
        }
        return
      }
      const rf = reactFlowInstance.current
      if (!rf) {
        log('focus: no reactFlowInstance, skipping')
        return
      }

      const activeEditor = activeTextEntryElement()
      if (activeEditor) {
        viewportFocusLog({
          event: 'viewport-focus-intent-suppressed-during-text-entry',
          trace_id: `canvas-viewport-focus:${projectId ?? ''}`,
          projectId: projectId ?? '',
          reason: 'active-text-entry',
          nodeId,
          ...describeElementForLog(activeEditor),
        })
        return
      }

      // For child nodes (e.g. panels inside a storyboard group), focus on
      // the parent group to show the full context instead of jumping between
      // individual children. fitView resolves absolute positions through the
      // ReactFlow parent chain, avoiding the bug where relative positions
      // were treated as absolute by setCenter.
      const focusId = target.parentId || nodeId
      log('focus: fitting view on %s (requested %s)', focusId, nodeId)
      rf.fitView({
        nodes: [{ id: focusId }],
        padding: 0.15,
        duration: 500,
        maxZoom: 1,
        minZoom: 0.3,
      })
    }

    let pendingFocusIntent: string | null = null
    let pendingFocusSource: string | undefined
    let focusIntentTimer: ReturnType<typeof setTimeout> | null = null

    const onFocusChange = () => {
      const nodeIdValue = yFocus.get('nodeId')
      const nodeId = typeof nodeIdValue === 'string' ? nodeIdValue : undefined
      if (!nodeId || !reactFlowInstance.current) {
        log('focus: observer fired but no nodeId or no reactFlowInstance')
        return
      }

      // Discriminate WHY this focus happened. Automatic completion focus
      // (source === 'auto') is suppressed while the user is interacting; any
      // other value — explicit Director focus ('director') or a legacy/undefined
      // value from an older backend — always moves the viewport, so we never
      // accidentally swallow an explicit focus (RFC: suppress auto-focus during
      // canvas interaction).
      const sourceValue = yFocus.get('source')
      const source = typeof sourceValue === 'string' ? sourceValue : undefined
      if (source === 'auto') {
        const target = nodesRef.current.find(n => n.id === nodeId)
        if (target && isDepthVideoExecutionNode(target)) {
          depthBatchLayoutLog({
            event: 'automatic-focus-suppressed',
            trace_id: `depth-batch-layout:${projectId ?? ''}`,
            project_id: projectId ?? '',
            trigger: 'completion-focus',
            reason: 'depth-execution-is-not-navigation',
            node_ids: nodeId,
            node_count: 1,
          })
          return
        }
        const sinceInteractionMs = Date.now() - lastInteractionTsRef.current
        if (sinceInteractionMs < CANVAS_AUTOFOCUS_COOLDOWN_MS) {
          autofocusLog({
            event: 'autofocus-skip',
            path: 'completion',
            reason: 'recent-interaction',
            sinceMs: sinceInteractionMs,
            nodeId,
          })
          // Do NOT set lastFocusIntentTs / schedule fitView — the viewport stays.
          return
        }
      }

      lastFocusIntentTs.current = Date.now()
      log('focus: intent received for node %s (source=%s)', nodeId, source ?? 'unset')

      // Debounce: when multiple focus intents arrive rapidly (e.g. 9 panel
      // completions in a storyboard), only the last one triggers viewport
      // movement. This prevents the viewport from jumping 9 times.
      pendingFocusIntent = nodeId
      pendingFocusSource = source
      if (focusIntentTimer) {
        clearTimeout(focusIntentTimer)
      }
      focusIntentTimer = setTimeout(() => {
        const pending = pendingFocusIntent
        const pendingSource = pendingFocusSource
        pendingFocusIntent = null
        pendingFocusSource = undefined
        focusIntentTimer = null
        if (!pending) return
        // Re-check the cooldown at fire time for automatic focus: the user may
        // have interacted during the 500ms debounce window above.
        if (pendingSource === 'auto') {
          const sinceFireMs = Date.now() - lastInteractionTsRef.current
          if (sinceFireMs < CANVAS_AUTOFOCUS_COOLDOWN_MS) {
            autofocusLog({
              event: 'autofocus-skip-at-fire',
              path: 'completion',
              reason: 'recent-interaction',
              sinceMs: sinceFireMs,
              nodeId: pending,
            })
            return
          }
        }
        autofocusLog({
          event: 'autofocus-fired',
          path: pendingSource === 'director' ? 'director' : 'completion',
          nodeId: pending,
        })
        scheduleFitView(pending, 0)
      }, 500)
    }
    yFocus.observe(onFocusChange)

    return () => {
      lifecycleLog('effect cleanup: detaching observers')
      yNodes.unobserveDeep(onNodesChange)
      yEdges.unobserveDeep(onEdgesChange)
      yFocus.unobserve(onFocusChange)
      yNodesRef.current = null
      if (autoFocusTimerRef.current) {
        clearTimeout(autoFocusTimerRef.current)
        autoFocusTimerRef.current = null
      }
      if (focusIntentTimer) {
        clearTimeout(focusIntentTimer)
        focusIntentTimer = null
      }
    }
  }, [ydoc, synced, isBackendConnected, setNodes, setEdges, nodesRef, edgesRef, isUndoRedo, reactFlowInstance, knownNodeTypes, selectedNodeIdsRef, projectId, lastInteractionTsRef])

  // --- Write helpers ---

  const writeNodePosition = useCallback((nodeId: string, x: number, y: number, tainted?: boolean) => {
    const yNodes = yNodesRef.current
    if (!yNodes) {
      lifecycleLog('writeNodePosition: yNodesRef is null, skipping %s', nodeId)
      return
    }

    const ynode = yNodes.get(nodeId)
    if (!ynode) {
      lifecycleLog('writeNodePosition: node not found in Y doc, skipping %s', nodeId)
      return
    }

    // Skip position writes for group children — their positions are computed
    // from in_child_node_ids order in readNodesFromYDoc, never stored.
    if (ynode.get('parentNode')) return

    log('writePosition node=%s x=%d y=%d tainted=%s (client=%d)', nodeId, x, y, tainted, ydoc.clientID)
    statusLog('position-write nodeId=%s x=%d y=%d source=drag', nodeId, x, y)
    const curX = yOptionalNumber(ynode, 'x')
    const curY = yOptionalNumber(ynode, 'y')
    if (curX === x && curY === y && (tainted === undefined || ynode.get('tainted') === tainted)) return
    ydoc.transact(() => {
      ynode.set('x', x)
      ynode.set('y', y)
      if (tainted !== undefined) {
        ynode.set('tainted', tainted)
      }
    }, 'frontend')
  }, [ydoc])

  const addNodeToYDoc = useCallback((
    nodeId: string,
    nodeType: string,
    label: string,
    x: number,
    y: number,
    data?: RawNodeDataUpdates,
    tainted?: boolean,
    parentId?: string,
  ) => {
    const yNodes = yNodesRef.current
    if (!yNodes) {
      lifecycleLog('addNodeToYDoc: yNodesRef is null, skipping %s', nodeId)
      return
    }

    if (yNodes.has(nodeId)) {
      return
    }

    ydoc.transact(() => {
      const ynode = new Y.Map<YDocValue>()
      ynode.set('type', nodeType)
      ynode.set('label', label)
      const initialData = prepareYDocCreationData(
        nodeType,
        data ? normalizeNodeDataUpdates(data) : undefined,
      )
      const resolvedStatus = typeof initialData?.status === 'string' ? initialData.status : 'idle'
      ynode.set('status', resolvedStatus)
      if (resolvedStatus !== 'idle') {
        libraryImportLog({
          trace_id: `library-import:${nodeType}`,
          event: 'status-preserved',
          nodeId,
          nodeType,
          status: resolvedStatus,
        })
      }
      ynode.set('x', x)
      ynode.set('y', y)
      ynode.set('tainted', !!tainted)
      if (parentId) {
        ynode.set('parentNode', parentId)
      }
      yNodes.set(nodeId, ynode)

      // Write initial data as per-field keys. The generated binder reads
      // `type`, so the Y.Map must be integrated into a Y.Doc first.
      if (initialData && nodeType !== 'group') {
        const backendType = toBackendType(nodeType)
        if (isGeneratedBackendNodeType(backendType)) {
          try {
            const writtenFields = bindRawCanvasNode(ynode, backendType).writeDataFields(initialData, {
              forceDesignerJsonFields: true,
            })
            const writtenFieldSet = new Set(writtenFields)
            libraryImportLog({
              trace_id: `library-import:${nodeType}`,
              event: 'initial-data-written',
              nodeId,
              nodeType,
              backendType,
              fieldCount: Object.keys(initialData).length,
              writtenFieldCount: writtenFields.length,
              wroteOutImage: writtenFieldSet.has('out_image'),
              writtenFields,
            })
          } catch (error) {
            yNodes.delete(nodeId)
            libraryImportLog({
              trace_id: `library-import:${nodeType}`,
              event: 'initial-data-write-failed',
              nodeId,
              nodeType,
              backendType,
              error: error instanceof Error ? error.message : String(error),
            })
            throw error
          }
        }
      }
    }, 'frontend')
  }, [ydoc])

  const removeNodeFromYDoc = useCallback((nodeId: string) => {
    const yNodes = yNodesRef.current
    if (!yNodes || !yNodes.has(nodeId)) return
    const yEdges = ydoc.getMap<YDocMap>('edges')
    ydoc.transact(() => {
      const affectedPorts: TargetPortRef[] = []
      // Remove all edges connected to this node
      yEdges.forEach((_yedge: YDocMap, edgeId: string) => {
        const source = yString(_yedge, 'source')
        const target = yString(_yedge, 'target')
        if (source === nodeId || target === nodeId) {
          if (source === nodeId && target !== nodeId) {
            rememberTargetPort(affectedPorts, target, yEdgeTargetPort(_yedge))
          }
          yEdges.delete(edgeId)
        }
      })
      for (const port of affectedPorts) {
        reindexTargetPortEdges(yEdges, port.target, port.targetHandle)
      }
      yNodes.delete(nodeId)
    }, 'frontend')
  }, [ydoc])

  const addEdgeToYDoc = useCallback((edge: { source: string; target: string; sourceHandle?: string; targetHandle?: string; toIndex?: number; fromIndex?: number; type?: string }) => {
    const yEdges = ydoc.getMap<YDocMap>('edges')
    const indexSuffix = edge.toIndex !== undefined ? `[${edge.toIndex}]` : ''
    const edgeId = edge.sourceHandle && edge.targetHandle
      ? `${edge.source}.${edge.sourceHandle}-${edge.target}.${edge.targetHandle}${indexSuffix}`
      : `${edge.source}-${edge.target}${indexSuffix}`
    ydoc.transact(() => {
      const edgeData = new Y.Map<YDocValue>()
      edgeData.set('source', edge.source)
      edgeData.set('target', edge.target)
      edgeData.set('type', edge.type ?? 'default')
      if (edge.sourceHandle) edgeData.set('from_port', edge.sourceHandle)
      if (edge.targetHandle) edgeData.set('to_port', edge.targetHandle)
      if (edge.toIndex !== undefined) edgeData.set('toIndex', edge.toIndex)
      if (edge.fromIndex !== undefined) edgeData.set('fromIndex', edge.fromIndex)
      yEdges.set(edgeId, edgeData)
    }, 'frontend')
  }, [ydoc])

  const removeEdgeFromYDoc = useCallback((edgeId: string) => {
    const yEdges = ydoc.getMap<YDocMap>('edges')
    ydoc.transact(() => {
      const yedge = yEdges.get(edgeId)
      if (!yedge) return
      const target = yString(yedge, 'target')
      const targetHandle = yEdgeTargetPort(yedge)
      yEdges.delete(edgeId)
      reindexTargetPortEdges(yEdges, target, targetHandle)
    }, 'frontend')
  }, [ydoc])

  const removeEdgesFromYDocByPorts = useCallback((edges: Array<{ source: string; target: string; sourceHandle?: string; targetHandle?: string }>) => {
    const yEdges = ydoc.getMap<YDocMap>('edges')
    ydoc.transact(() => {
      const affectedPorts: TargetPortRef[] = []
      for (const e of edges) {
        const toDelete: string[] = []
        yEdges.forEach((_yedge: YDocMap, edgeId: string) => {
          const src = yString(_yedge, 'source')
          const tgt = yString(_yedge, 'target')
          const srcH = yEdgeSourcePort(_yedge)
          const tgtH = yEdgeTargetPort(_yedge)
          if (src === e.source && tgt === e.target &&
              (!e.sourceHandle || srcH === e.sourceHandle) &&
              (!e.targetHandle || tgtH === e.targetHandle)) {
            rememberTargetPort(affectedPorts, tgt, tgtH)
            toDelete.push(edgeId)
          }
        })
        for (const edgeId of toDelete) {
          yEdges.delete(edgeId)
        }
      }
      for (const port of affectedPorts) {
        reindexTargetPortEdges(yEdges, port.target, port.targetHandle)
      }
    }, 'frontend')
  }, [ydoc])

  const writeNodeData = useCallback(<T extends FeNodeType>(
    nodeRef: ReactFlowNodeRef<T>,
    updates: NodeDataUpdates<T>,
  ) => {
    const nodeId = nodeRef.id
    const yNodes = yNodesRef.current
    if (!yNodes) return

    const ynode = yNodes.get(nodeId)
    if (!ynode) return

    const feType = yString(ynode, 'type')
    if (feType !== nodeRef.type) {
      throw new Error(`writeNodeData type mismatch for ${nodeId}: expected ${nodeRef.type}, found ${feType}`)
    }
    const backendType = toBackendType(feType)
    assertNoDirectAssetInputPortWrites(feType, nodeId, updates as RawNodeDataUpdates, 'writeNodeData')
    assertNoBackendAuthoredOutputMapWrites(feType, nodeId, updates as RawNodeDataUpdates, 'writeNodeData')
    validateTypedCanvasNode(ynode, backendType)
    const ydocNode = isGeneratedBackendNodeType(backendType)
      ? bindRawCanvasNode(ynode, backendType)
      : undefined
    const rawUpdates = updates as RawNodeDataUpdates
    const updatesToWrite = normalizeNodeDataUpdates(rawUpdates)

    ydoc.transact(() => {
      if (ydocNode) {
        for (const key of ydocNode.writeDataFields(updatesToWrite)) {
          ydocWriteLog('write nodeId=%s key=%s', nodeId, key)
        }
        applyDesignerPickedIndexPatches(updatesToWrite, ynode, nodeId, backendType)
      }

      // Promote well-known fields to top-level YDoc keys
      if ('name' in updatesToWrite) {
        const v = updatesToWrite.name
        if (typeof v === 'string') {
          if (ynode.get('label') !== v) {
            ynode.set('label', v)
            ydocWriteLog('write nodeId=%s key=%s', nodeId, 'label (from name)')
          } else {
            ydocWriteLog('skip same-value nodeId=%s key=%s', nodeId, 'label (from name)')
          }
        }
      }
      if ('label' in updatesToWrite && typeof updatesToWrite.label === 'string') {
        if (ynode.get('label') !== updatesToWrite.label) {
          ynode.set('label', updatesToWrite.label)
          ydocWriteLog('write nodeId=%s key=%s', nodeId, 'label')
        } else {
          ydocWriteLog('skip same-value nodeId=%s key=%s', nodeId, 'label')
        }
      }
      if ('status' in updatesToWrite && typeof updatesToWrite.status === 'string') {
        if (ynode.get('status') !== updatesToWrite.status) {
          ynode.set('status', updatesToWrite.status)
          ydocWriteLog('write nodeId=%s key=%s', nodeId, 'status')
        } else {
          ydocWriteLog('skip same-value nodeId=%s key=%s', nodeId, 'status')
        }
      }
    }, 'frontend')
    validateTypedCanvasNode(ynode, backendType)
  }, [ydoc])

  const flushNodeInputFromEdgeRefs = useCallback((
    targetId: string,
    targetHandle: string,
    nodes: readonly YDocInputFlushNode[],
    edgeRefs: readonly YDocInputFlushEdgeRef[],
  ): YDocInputFlushResult | undefined => {
    const yNodes = yNodesRef.current
    if (!yNodes) return undefined

    const ynode = yNodes.get(targetId)
    if (!ynode) return undefined

    const targetNode = nodes.find((node) => node.id === targetId)
    if (!targetNode) return undefined
    if (!isFeNodeType(targetNode.type)) return undefined
    const targetYNode = bindCanvasNodeByFeType(ynode, targetNode.type)

    let result: YDocInputFlushResult | undefined
    ydoc.transact(() => {
      result = targetYNode.flushInputFromEdgeRefs(targetHandle, nodes, edgeRefs)
      if (result?.written) {
        ydocWriteLog('generated flush nodeId=%s key=%s', targetId, result.field)
      }
    }, 'frontend')
    return result
  }, [ydoc])

  const writeNodeDimensions = useCallback((_nodeId: string, _width: number, _height: number) => {
    // Dimension sync removed — writing rendered DOM dimensions to YDoc caused
    // oscillation loops when passthrough deserializers deliver raw Y.Map/Y.Array
    // references (each YDoc write triggers an observe → re-render → re-measure
    // → re-write cycle). Schema layout dimensions are the source of truth;
    // the frontend reads them from the node schema at render time.
  }, [])

  const writeNodeParent = useCallback((nodeId: string, parentId: string | null) => {
    const yNodes = yNodesRef.current
    if (!yNodes) return

    const ynode = yNodes.get(nodeId)
    if (!ynode) return

    ydoc.transact(() => {
      if (parentId) {
        ynode.set('parentNode', parentId)
      } else {
        ynode.delete('parentNode')
      }
    }, 'frontend')
  }, [ydoc])

  // Cross-instance node status sync: listen for dw:node-status events
  // dispatched by the chat WebSocket handler (via Redis Pub/Sub) and apply
  // the status directly to the local YDoc so the canvas updates.
  //
  // Also: refresh the Sparks balance when a non-director node reaches a
  // charge/refund lifecycle status, and refresh the project cost summary
  // when that node reaches a terminal status. Backend broadcasts
  // `node_status` for every status change in single-instance dev too, so
  // this is the canonical hook for both local and multi-instance behaviour.
  useEffect(() => {
    const sparksInvalidateNodeIds = sparksInvalidateNodeIdsRef.current
    const handlerScope: ActiveCanvasScope = {
      ydoc,
      projectId,
      canvasId: canvasId ?? 'agent',
    }
    const isHandlerScopeCurrent = () => {
      const currentScope = activeCanvasScopeRef.current
      return (
        currentScope.ydoc === handlerScope.ydoc &&
        currentScope.projectId === handlerScope.projectId &&
        currentScope.canvasId === handlerScope.canvasId
      )
    }
    const handler = (e: Event) => {
      if (!isHandlerScopeCurrent()) {
        statusLog('node-status relay skipped: stale listener scope project=%s canvas=%s', handlerScope.projectId ?? '', handlerScope.canvasId)
        return
      }
      if (!(e instanceof CustomEvent) || !isRecord(e.detail)) return
      const nodeId = typeof e.detail.nodeId === 'string' ? e.detail.nodeId : undefined
      const status = isNodeStatus(e.detail.status) ? e.detail.status : undefined
      const hasError = Object.prototype.hasOwnProperty.call(e.detail, 'error')
      const error =
        typeof e.detail.error === 'string'
          ? e.detail.error
          : e.detail.error === null
            ? null
            : undefined
      const hasAction = Object.prototype.hasOwnProperty.call(e.detail, 'action')
      const action =
        typeof e.detail.action === 'string'
          ? e.detail.action
          : e.detail.action === null
            ? null
            : undefined
      if (!nodeId || !status) return
      sparksRefreshLog({
        event: 'credit-balance-refresh-trigger-received',
        feature: 'credit-balance',
        trigger: 'node-status-relay',
        node_id: nodeId,
        next_status: status,
      })
      const yNodes = yNodesRef.current
      if (!yNodes) {
        sparksRefreshLog({
          event: 'credit-balance-refresh-skipped',
          feature: 'credit-balance',
          trigger: 'node-status-relay',
          reason: 'ydoc-not-ready',
          node_id: nodeId,
          next_status: status,
        })
        return
      }
      const ynode = yNodes.get(nodeId)
      if (!ynode) {
        sparksRefreshLog({
          event: 'credit-balance-refresh-skipped',
          feature: 'credit-balance',
          trigger: 'node-status-relay',
          reason: 'node-not-in-ydoc',
          node_id: nodeId,
          next_status: status,
        })
        return
      }
      const nodeType = yOptionalString(ynode, 'type')
      if (nodeType === 'director') {
        sparksRefreshLog({
          event: 'credit-balance-refresh-skipped',
          feature: 'credit-balance',
          trigger: 'node-status-relay',
          reason: 'director',
          node_id: nodeId,
          node_type: nodeType,
          next_status: status,
        })
      } else if (!SPARKS_REFRESH_STATUSES.has(status)) {
        sparksRefreshLog({
          event: 'credit-balance-refresh-skipped',
          feature: 'credit-balance',
          trigger: 'node-status-relay',
          reason: 'ineligible-status',
          node_id: nodeId,
          node_type: nodeType,
          next_status: status,
        })
      } else {
        sparksInvalidateBatchedRef.current += 1
        sparksInvalidateNodeIds.add(nodeId)
        sparksRefreshLog({
          event: 'credit-balance-refresh-scheduled',
          feature: 'credit-balance',
          source: 'ws-relay',
          trigger: 'node-status-relay',
          reason: 'charge-or-refund-status',
          node_id: nodeId,
          node_type: nodeType,
          next_status: status,
          pendingCount: sparksInvalidateBatchedRef.current,
        })
        if (sparksInvalidateTimerRef.current) {
          clearTimeout(sparksInvalidateTimerRef.current)
        }
        sparksInvalidateTimerRef.current = setTimeout(() => {
          if (!isHandlerScopeCurrent()) return
          const batchedCount = sparksInvalidateBatchedRef.current
          const nodeIds = [...sparksInvalidateNodeIds].sort()
          sparksInvalidateBatchedRef.current = 0
          sparksInvalidateNodeIds.clear()
          sparksInvalidateTimerRef.current = null
          sparksRefreshLog({
            event: 'credit-balance-query-invalidated',
            feature: 'credit-balance',
            source: 'ws-relay',
            trigger: 'node-status-relay',
            reason: 'charge-or-refund-status',
            target_type: 'react-query-cache',
            target_id: 'queryKeys.sparks',
            node_ids: nodeIds.join(','),
            pendingCount: batchedCount,
            target_count: nodeIds.length,
          })
          void queryClient.invalidateQueries({ queryKey: queryKeys.sparks }).then(() => {
            // With React Query's default throwOnError=false, settlement proves
            // cache invalidation completed, not that an active refetch succeeded.
            sparksRefreshLog({
              event: 'credit-balance-query-invalidation-settled',
              feature: 'credit-balance',
              target_type: 'react-query-cache',
              target_id: 'queryKeys.sparks',
              outcome: 'settled',
            })
          })
        }, 250)
      }
      if (
        ynode.get('status') === status &&
        (!hasError || ynode.get('error') === error) &&
        (!hasAction || ynode.get('action') === action)
      ) return
      ydoc.transact(() => {
        ynode.set('status', status)
        if (hasError) {
          ynode.set('error', error ?? null)
        }
        if (hasAction) {
          ynode.set('action', action ?? null)
        }
      }, 'backend-relay')

      // Cost summary invalidation — terminal-only, non-director, project-scoped.
      const isTerminal = status === 'completed' || status === 'error'
      if (!isTerminal || nodeType === 'director' || !projectId) {
        if (isTerminal && !projectId) {
          costInvalidateLog({ event: 'skipped', reason: 'no-projectId', nodeId, nodeType, status })
        }
        return
      }
      costInvalidateBatchedRef.current += 1
      costInvalidateLog({
        event: 'schedule',
        projectId,
        source: 'ws-relay',
        nodeId,
        nodeType,
        newStatus: status,
        batched: costInvalidateBatchedRef.current,
      })
      if (costInvalidateTimerRef.current) {
        clearTimeout(costInvalidateTimerRef.current)
      }
      costInvalidateTimerRef.current = setTimeout(() => {
        if (!isHandlerScopeCurrent()) return
        const batchedCount = costInvalidateBatchedRef.current
        costInvalidateBatchedRef.current = 0
        costInvalidateTimerRef.current = null
        costInvalidateLog({ event: 'invalidate', projectId, batchedCount })
        queryClient.invalidateQueries({ queryKey: queryKeys.costSummary(projectId) })
      }, 250)
    }
    window.addEventListener('dw:node-status', handler)
    return () => {
      window.removeEventListener('dw:node-status', handler)
      if (costInvalidateTimerRef.current) {
        clearTimeout(costInvalidateTimerRef.current)
        costInvalidateTimerRef.current = null
      }
      if (sparksInvalidateTimerRef.current) {
        clearTimeout(sparksInvalidateTimerRef.current)
        sparksInvalidateTimerRef.current = null
      }
      sparksInvalidateBatchedRef.current = 0
      sparksInvalidateNodeIds.clear()
    }
  }, [ydoc, projectId, canvasId, queryClient])

  useEffect(() => {
    let active = true
    const handlerScope: ActiveCanvasScope = {
      ydoc,
      projectId,
      canvasId: canvasId ?? 'agent',
    }
    const isHandlerScopeCurrent = () => {
      const currentScope = activeCanvasScopeRef.current
      return (
        currentScope.ydoc === handlerScope.ydoc &&
        currentScope.projectId === handlerScope.projectId &&
        currentScope.canvasId === handlerScope.canvasId
      )
    }
    const handler = (e: Event) => {
      if (!isHandlerScopeCurrent()) {
        nodeResyncLog({
          event: 'skipped',
          reason: 'stale-listener-scope',
          project_id: handlerScope.projectId ?? '',
          canvas_id: handlerScope.canvasId,
        })
        return
      }
      if (!(e instanceof CustomEvent) || !isRecordLike(e.detail)) return
      const eventProjectId = typeof e.detail.projectId === 'string' ? e.detail.projectId : undefined
      const eventCanvasId =
        typeof e.detail.canvasId === 'string' && e.detail.canvasId.length > 0
          ? e.detail.canvasId
          : 'agent'
      if (!handlerScope.projectId || eventProjectId !== handlerScope.projectId || eventCanvasId !== handlerScope.canvasId) {
        nodeResyncLog({
          event: 'skipped',
          reason: 'scope-mismatch',
          project_id: handlerScope.projectId ?? '',
          event_project_id: eventProjectId ?? '',
          canvas_id: handlerScope.canvasId,
          event_canvas_id: eventCanvasId,
        })
        return
      }
      const nodeIds = Array.isArray(e.detail.nodeIds)
        ? e.detail.nodeIds.filter((value): value is string => typeof value === 'string' && value.length > 0)
        : []
      if (nodeIds.length === 0) return
      const seq = ++nodeResyncSeqRef.current
      for (const nodeId of nodeIds) {
        nodeResyncSeqByNodeRef.current.set(nodeId, seq)
      }
      const resyncReason = typeof e.detail.reason === 'string' ? e.detail.reason : ''
      const traceId =
        typeof e.detail.traceId === 'string' && e.detail.traceId.length > 0
          ? e.detail.traceId
          : undefined
      const traceFields = traceId ? { trace_id: traceId } : {}
      nodeResyncLog({
        ...traceFields,
        event: 'requested',
        seq,
        project_id: handlerScope.projectId,
        canvas_id: eventCanvasId,
        node_ids: nodeIds.join(','),
        reason: resyncReason,
      })

      void getCanvasState(handlerScope.projectId, eventCanvasId)
        .then((state) => {
          if (!active || !isHandlerScopeCurrent()) {
            nodeResyncLog({
              event: 'skipped',
              seq,
              reason: active ? 'stale-listener-scope' : 'scope-disposed',
              node_ids: nodeIds.join(','),
            })
            return
          }
          const yNodes = yNodesRef.current
          if (!yNodes) {
            nodeResyncLog({
              event: 'skipped',
              seq,
              reason: 'ydoc-not-ready',
              node_ids: nodeIds.join(','),
            })
            return
          }

          const updatedNodeIds: string[] = []
          ydoc.transact(() => {
            for (const nodeId of nodeIds) {
              if (nodeResyncSeqByNodeRef.current.get(nodeId) !== seq) {
                nodeResyncLog({
                  ...traceFields,
                  event: 'skipped-node',
                  seq,
                  reason: 'superseded',
                  node_id: nodeId,
                })
                continue
              }
              const serverNode = state.nodes.find(
                (candidate): candidate is Record<string, unknown> => (
                  isRecordLike(candidate) && candidate.id === nodeId
                ),
              )
              const ynode = yNodes.get(nodeId)
              if (!serverNode || !ynode) continue
              const feType = yOptionalString(ynode, 'type')
              if (!isFeNodeType(feType)) continue
              const backendType = toBackendType(feType)
              if (!isGeneratedBackendNodeType(backendType)) continue
              const serverType = typeof serverNode.type === 'string' ? serverNode.type : undefined
              if (serverType && serverType !== feType && serverType !== backendType) {
                nodeResyncLog({
                  ...traceFields,
                  event: 'skipped-node',
                  seq,
                  reason: 'type-mismatch',
                  node_id: nodeId,
                  local_type: feType,
                  server_type: serverType,
                })
                continue
              }
              const serverData = isRecordLike(serverNode.data) ? serverNode.data : {}
              const declaredFields = CANVAS_NODE_FIELD_TYPES[backendType]
              const stateFields = NODE_STATE_FIELDS[backendType] ?? new Set<string>()
              const updates: RawNodeDataUpdates = {}
              for (const [key, value] of Object.entries(serverData)) {
                if (
                  Object.prototype.hasOwnProperty.call(declaredFields, key) ||
                  stateFields.has(key) ||
                  RESYNC_COMMON_DATA_FIELDS.has(key)
                ) {
                  updates[key] = value as ReactFlowFieldValue
                }
              }

              let normalizedUpdates: RawNodeDataUpdates
              try {
                normalizedUpdates = validateResyncNodeDataUpdates(feType, backendType, updates)
              } catch (error) {
                nodeResyncLog({
                  ...traceFields,
                  event: 'skipped-node',
                  seq,
                  reason: 'invalid-server-data',
                  node_id: nodeId,
                  error: error instanceof Error ? error.message : String(error),
                })
                continue
              }

              const serverStatus = isNodeStatus(serverData.status)
                ? serverData.status
                : isNodeStatus(serverNode.status)
                  ? serverNode.status
                  : undefined
              const serverLabel =
                typeof serverData.name === 'string'
                  ? serverData.name
                  : typeof serverNode.label === 'string'
                    ? serverNode.label
                    : undefined

              const ydocNode = bindRawCanvasNode(ynode, backendType)
              const writtenFields = ydocNode.writeDataFields(normalizedUpdates, {
                forceDesignerJsonFields: true,
              })
              applyDesignerPickedIndexPatches(updates, ynode, nodeId, backendType)
              if (serverStatus && ynode.get('status') !== serverStatus) {
                ynode.set('status', serverStatus)
              }
              if (serverLabel && ynode.get('label') !== serverLabel) {
                ynode.set('label', serverLabel)
              }
              readNodeDataFromYMapSafe(ynode, nodeId)
              updatedNodeIds.push(nodeId)
              nodeResyncLog({
                ...traceFields,
                event: 'node-updated',
                seq,
                node_id: nodeId,
                backend_type: backendType,
                written_fields: writtenFields.join(','),
              })
            }
          }, 'backend-relay')

          nodeResyncLog({
            ...traceFields,
            event: 'completed',
            seq,
            requested_count: nodeIds.length,
            updated_count: updatedNodeIds.length,
            node_ids: updatedNodeIds.join(','),
          })
        })
        .catch((error) => {
          if (!active) return
          nodeResyncLog({
            ...traceFields,
            event: 'failed',
            seq,
            node_ids: nodeIds.join(','),
            error: error instanceof Error ? error.message : String(error),
          })
        })
    }
    window.addEventListener('dw:canvas-node-resync', handler)
    return () => {
      active = false
      window.removeEventListener('dw:canvas-node-resync', handler)
    }
  }, [canvasId, projectId, ydoc])

  return { ready, ydocRunningCount, ydocQueuedCount, ydocDirectorRunning, ydocStatusLive: ready && synced && isBackendConnected, writeNodePosition, addNodeToYDoc, removeNodeFromYDoc, writeNodeData, flushNodeInputFromEdgeRefs, writeNodeDimensions, writeNodeParent, addEdgeToYDoc, removeEdgeFromYDoc, removeEdgesFromYDocByPorts }
}

// ---------------------------------------------------------------------------
// useYViewport: local browser storage → Viewport + write helper
// ---------------------------------------------------------------------------

interface UseYViewportResult {
  /** Viewport from local browser storage (null until synced or absent). */
  viewport: Viewport | null
  /** Write viewport changes to local browser storage. */
  writeViewport: (viewport: Viewport) => void
}

function parseStoredViewport(raw: string | null): Viewport | null {
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!isRecordLike(parsed)) return null
    const x = parsed.x
    const y = parsed.y
    const zoom = parsed.zoom
    if (typeof x !== 'number' || typeof y !== 'number' || typeof zoom !== 'number') {
      return null
    }
    return { x, y, zoom }
  } catch {
    return null
  }
}

/**
 * Read the stored viewport synchronously.
 *
 * Exported because `WorkspacePageV2`'s fitView fallback has to consult storage
 * *during the same commit* that `synced` flips true. The `viewport` state
 * returned by `useYViewport` is one render behind at that moment (the effect
 * below has only been scheduled, not run), and acting on that lag is what made
 * a reconnect blow away the user's pan/zoom.
 */
export function readStoredViewport(storageKey: string): Viewport | null {
  if (typeof window === 'undefined') return null
  try {
    return parseStoredViewport(window.localStorage.getItem(storageKey))
  } catch {
    return null
  }
}

/**
 * A viewport of exactly origin/1x carries no user intent — it is what an
 * untouched canvas reports. Restoring it is indistinguishable from doing
 * nothing, so callers treat it as "no saved viewport" and let fitView run.
 */
export function isDefaultViewport(viewport: Viewport): boolean {
  return viewport.x === 0 && viewport.y === 0 && viewport.zoom === 1
}

/** What the canvas should do with its viewport once a room reaches `synced`. */
export type ViewportSyncPlan =
  | { action: 'restore'; viewport: Viewport }
  | { action: 'fitview' }
  | { action: 'none' }

/**
 * Decide the viewport for a freshly synced canvas, reading storage inline.
 *
 * Synchronous on purpose. The caller runs in the same commit where `synced`
 * flips true, and at that point `useYViewport`'s `viewport` state is one render
 * stale — it has only scheduled its re-read. Deciding from that stale state is
 * what let a reconnect replace the user's pan with a fitView.
 *
 * Returns `restore` with the viewport rather than a bare "don't fitView"
 * boolean so the caller can apply it immediately. Merely deferring to the
 * restore effect would be a bet that `useYViewport` re-reads the same value,
 * and it does not always: its effect only re-runs on `[storageKey, synced]`, so
 * a write from another tab after that read would leave the caller deferring to
 * a restore that never comes — no fitView either, and a canvas stranded at the
 * ReactFlow default.
 *
 * A stored viewport of exactly origin/1x carries no user intent — it is what an
 * untouched canvas reports — so it is treated as nothing stored and fitView
 * runs instead. Caveat worth knowing: the separate restore effect latches on a
 * stored default without applying it, so if that effect runs first the fitView
 * planned here never happens. That is a pre-existing bug, tracked in RFC-1386
 * follow-up 1, not something this function can decide its way out of.
 */
export function planViewportOnSync(
  storageKey: string | null,
  nodeCount: number,
): ViewportSyncPlan {
  const stored = storageKey ? readStoredViewport(storageKey) : null
  if (stored !== null && !isDefaultViewport(stored)) {
    return { action: 'restore', viewport: stored }
  }
  // fitView has nothing to frame on an empty canvas.
  return nodeCount > 0 ? { action: 'fitview' } : { action: 'none' }
}

function writeStoredViewport(storageKey: string, viewport: Viewport): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(viewport))
  } catch {
    // Viewport is a local UI preference. Storage failures must not break canvas use.
  }
}

function sameViewport(a: Viewport | null, b: Viewport): boolean {
  return (
    a !== null &&
    Math.abs(a.x - b.x) < 0.01 &&
    Math.abs(a.y - b.y) < 0.01 &&
    Math.abs(a.zoom - b.zoom) < 0.0001
  )
}

export function useYViewport(storageKey: string | null, synced: boolean): UseYViewportResult {
  const [viewport, setViewport] = useState<Viewport | null>(null)

  useEffect(() => {
    if (!synced || !storageKey) {
      setViewport(null)
      return
    }
    setViewport(readStoredViewport(storageKey))
  }, [storageKey, synced])

  const writeViewport = useCallback((vp: Viewport) => {
    if (!synced || !storageKey) return
    if (sameViewport(viewport, vp)) return
    writeStoredViewport(storageKey, vp)
    setViewport(vp)
  }, [storageKey, synced, viewport])

  return { viewport, writeViewport }
}
